import { useState, useLayoutEffect } from "react";
import CSDTable from "@cloudscape-design/components-themed/table";
import { CONFIGS, LABELS } from "./constants";
import { Box } from "@cloudscape-design/components-themed";
import { COMMON_LABELS } from "../../default-constants";
import Preference from "../../UI/Preference";
import { DEFAULT_PAGE_SIZE } from "../../../configs/constants";
import { getHeaderCounterText, getPagedItems } from "../../../utils/common";
import Pagination from "../../UI/Pagination";
import { Message } from "../../../features/api-interfaces";
import { Messages, getMessage } from "../../../features/error/messages";

export interface MessageItem {
  no: number;
  messageLabel: string;
  errorOccurredPlace: string;
}

/**
 * エラーメッセージ作成処理
 * @param messageList メッセージリスト
 * @returns エラーメッセージリスト
 */
export const getMessageItemList = (messageList: Message[]) => {
  // エラーメッセージリスト作成
  let errorMessageList: MessageItem[] = [];

  for (const [index, message] of messageList.entries()) {
    const msgId = message.messageId as keyof Messages;
    const messageStr = getMessage(msgId, ...message.messageArgs).label;
    const separatorIndex = messageStr.includes(CONFIGS.SEPARATOR)
      ? messageStr.lastIndexOf(CONFIGS.SEPARATOR)
      : undefined;
    const errorData = getMessageErrorData(messageStr, index, separatorIndex);
    errorMessageList.push(errorData);
  }

  return errorMessageList;
};

/**
 * エラーメッセージリスト用のオブジェクトに変換する
 * @param {string} messageStr エラーメッセージ
 * @param {number} index index
 * @param {number | undefined} separatorIndex エラーメッセージの分割index
 * @returns {MessageItem} エラーメッセージリスト用のオブジェクト
 */
const getMessageErrorData = (
  messageStr: string,
  index: number,
  separatorIndex: number | undefined
): MessageItem => {
  const defaultErrorData: MessageItem = {
    no: index + 1,
    messageLabel: messageStr,
    errorOccurredPlace: "",
  };

  if (separatorIndex !== undefined) {
    return {
      ...defaultErrorData,
      messageLabel: messageStr.slice(0, separatorIndex),
      errorOccurredPlace: messageStr.slice(separatorIndex),
    };
  }

  return defaultErrorData;
};

/**
 * エラー発生時のテーブルを表示する。
 *
 * @param props.data エラーデータリスト（MessageItem[]型）
 * @param props.title エラー表示タイトル
 * @returns エラーメッセージ表示テーブル
 */
const ErrorTable = (props: any) => {
  const columnDefinitions = [
    {
      id: "no",
      header: LABELS.NO,
      cell: (e: any) => e.no,
      width: 110,
    },
    {
      id: "messageLabel",
      header: LABELS.ERROR_DETAIL,
      cell: (e: any) => e.messageLabel,
    },
    {
      id: "errorOccurredPlace",
      header: LABELS.ERROR_OCCURRED_PLACE,
      cell: (e: any) => e.errorOccurredPlace,
    },
  ];
  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });

  const [pagedItems, setPagedItems] = useState<Object[]>(
    getPagedItems(props.data, 1, DEFAULT_PAGE_SIZE)
  );
  const [allItems, setAllItems] = useState<Object[]>(props.data);

  useLayoutEffect(() => {
    if (props.data !== undefined) {
      setPagedItems(getPagedItems(props.data, 1, DEFAULT_PAGE_SIZE));
      setAllItems(props.data);
      setCurrentPageIndex(1);
    }
  }, [props.data]);

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    if (props.data !== undefined) {
      setPagedItems(props.data.slice(0, newPageSize));
      setCurrentPageIndex(1);
    }
  };

  return (
    <CSDTable
      columnDefinitions={columnDefinitions}
      items={pagedItems}
      resizableColumns
      stickyHeader
      header={
        <Box color="text-status-error" variant="h2">
          {props.title}
          {getHeaderCounterText(
            allItems.length,
            pagedItems.length + (currentPageIndex - 1) * preferences.pageSize
          )}
        </Box>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          filteredItems={allItems}
          setCurrentPageIndex={setCurrentPageIndex}
          pageSize={preferences.pageSize}
          setPagedItems={setPagedItems}
        />
      }
      preferences={
        <Preference
          preferences={preferences}
          options={options}
          handleOnConfirm={handleOnConfirm}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          {COMMON_LABELS.CSD.NO_DATA}
        </Box>
      }
    />
  );
};

export default ErrorTable;
